import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import SkeletonLoader from '../../components/SkeletonLoader';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import apiClient from "../../api/axiosConfig";
import { API_ENDPOINTS } from "../../constants";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const NewsletterAnalytics = () => {
  const { addToast } = useToast();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNewsletterAnalytics = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(
        API_ENDPOINTS.NEWSLETTER_ANALYTICS,
        {
          params: {
            page,
          },
        }
      );

      if (response.data.success) {
        setAnalyticsData(response.data.data);
      } else {
        const errorMessage =
          response.data.message ||
          'Failed to fetch newsletter analytics.';

        addToast(errorMessage, 'error');
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        'Error fetching newsletter analytics.';

      addToast(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchNewsletterAnalytics(1);
  }, [fetchNewsletterAnalytics]);

  if (loading && !analyticsData) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <p className="text-red-500 text-center">{error}</p>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <p className="text-text-gray text-center">
          No analytics data available for the newsletter.
        </p>
      </div>
    );
  }

  const subscriberChartData = {
    labels: ['Active Subscribers', 'Unsubscribed'],
    datasets: [
      {
        data: [
          analyticsData.summary.activeSubscribers,
          analyticsData.summary.unsubscribed
        ],
        backgroundColor: ['#34D399', '#EF4444'],
        borderColor: ['#10B981', '#DC2626'],
        borderWidth: 1,
      },
    ],
  };

  const subscriberChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#9CA3AF',
        },
      },
      title: {
        display: true,
        text: 'Subscriber Status',
        color: '#FFFFFF',
      },
    },
  };

  const growthChartData = {
    labels: Object.keys(analyticsData.subscriberGrowth || {}),
    datasets: [
      {
        label: 'New Subscribers',
        data: Object.values(analyticsData.subscriberGrowth || {}),
        backgroundColor: '#60A5FA',
        borderColor: '#3B82F6',
        borderWidth: 1,
      },
    ],
  };

  const growthChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Subscriber Growth (Last 6 Months)',
        color: '#FFFFFF',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of New Subscribers',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
          stepSize: 1,
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
    },
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <h1 className="text-3xl font-bold text-text-white mb-6">
        Newsletter Analytics
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center">
          <div className="text-4xl font-bold text-accent-red">
            {analyticsData.summary.totalSubscribers}
          </div>
          <div className="text-lg text-text-gray">
            Total Subscribers
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center">
          <div className="text-4xl font-bold text-green-500">
            {analyticsData.summary.overallOpenRate}%
          </div>
          <div className="text-lg text-text-gray">
            Overall Open Rate
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center">
          <div className="text-4xl font-bold text-blue-500">
            {analyticsData.summary.overallClickRate}%
          </div>
          <div className="text-lg text-text-gray">
            Overall Click Rate
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center">
          <div className="text-4xl font-bold text-yellow-500">
            {analyticsData.summary.totalEmailsSent}
          </div>
          <div className="text-lg text-text-gray">
            Total Emails Sent
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[350px]">
          <Pie
            data={subscriberChartData}
            options={subscriberChartOptions}
          />
        </div>

        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[350px]">
          <Bar
            data={growthChartData}
            options={growthChartOptions}
          />
        </div>
      </div>

      <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle mb-8">
        <h2 className="text-xl font-bold text-text-white mb-4">
          Most Engaging Blogs
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800 border border-gray-700">
            <thead>
              <tr>
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">
                  Blog Title
                </th>

                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">
                  Opens
                </th>

                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">
                  Clicks
                </th>
              </tr>
            </thead>

            <tbody>
              {analyticsData.mostEngagingBlogs?.length > 0 ? (
                analyticsData.mostEngagingBlogs.map((blog) => (
                  <tr
                    key={blog.id}
                    className="hover:bg-gray-700"
                  >
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {blog.title}
                    </td>

                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {blog.opens}
                    </td>

                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {blog.clicks}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="py-3 px-4 text-center text-sm text-text-gray"
                  >
                    No blog engagement data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsletterAnalytics;